import { computed, ref } from 'vue'

// Estado global compartido - singleton para evitar múltiples inicializaciones
let isInitialized = false
const isDark = ref(false) // Mover fuera para que sea compartido globalmente
const STORAGE_KEY = 'poseidonie-theme'

/**
 * Composable simplificado usando funcionalidades nativas del navegador
 * y PrimeVue para gestión de temas
 */
export function useTheme() {
  
  // Aplicar tema al DOM
  const applyTheme = (dark: boolean) => {
    const root = document.documentElement
    
    if (dark) {
      root.classList.add('dark')
      root.setAttribute('data-theme', 'dark')
    } else {
      root.classList.remove('dark')
      root.setAttribute('data-theme', 'light')
    }
    
    // Guardar preferencia
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
    
    // Emitir evento para otros componentes
    window.dispatchEvent(new CustomEvent('theme-changed', { 
      detail: { theme: dark ? 'dark' : 'light', isDark: dark } 
    }))
  }
  
  // Inicializar tema - solo se ejecuta una vez globalmente
  const initTheme = () => {
    if (isInitialized) return
    isInitialized = true
    
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      isDark.value = saved === 'dark'
    } else {
      // Siempre inicializar en modo light
      isDark.value = false
    }
    
    applyTheme(isDark.value)
    
    // Escuchar cambios en la preferencia del sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        isDark.value = e.matches
        applyTheme(isDark.value)
      }
    })
  }
  
  // Toggle simple y efectivo
  const toggleTheme = () => {
    isDark.value = !isDark.value
    applyTheme(isDark.value)
  }
  
  // Establecer tema específico
  const setTheme = (theme: 'light' | 'dark') => {
    isDark.value = theme === 'dark'
    applyTheme(isDark.value)
  }
  
  // Computed properties para UI
  const currentTheme = computed(() => isDark.value ? 'dark' : 'light')
  const themeIcon = computed(() => isDark.value ? 'pi pi-sun' : 'pi pi-moon')
  // themeLabel ahora debe obtenerse desde i18n en el componente
  const themeMode = computed(() => isDark.value ? 'light' : 'dark') // El modo al que cambiaría
  
  // Utilidades para componentes
  const getThemeClass = (lightClass: string, darkClass: string) => {
    return isDark.value ? darkClass : lightClass
  }
  
  const getThemeValue = <T>(lightValue: T, darkValue: T): T => {
    return isDark.value ? darkValue : lightValue
  }
  
  return {
    // Estado
    isDark,
    currentTheme,
    themeIcon,
    themeMode,
    
    // Acciones
    toggleTheme,
    setTheme,
    initTheme,
    
    // Utilidades
    getThemeClass,
    getThemeValue,
  }
}